// src/components/common/EnhancedAuthModal.js
import React, { useState, useEffect, useRef } from 'react';
import authService from '../../services/authService';
import { User, Mail, Lock, Building, GraduationCap, X, AlertCircle, CheckCircle, RefreshCw, Upload, FileText, Clock } from 'lucide-react';

// ── Reusable file upload box ──────────────────────────────────────────────────

const FileUploadBox = ({ file, error, inputRef, onChange, hint }) => (
  <div>
    <div
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
        file ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 bg-white'
      }`}
    >
      {file ? (
        <div className="flex items-center justify-center gap-2 text-blue-700">
          <FileText className="w-5 h-5" />
          <span className="text-sm font-medium truncate max-w-[220px]">{file.name}</span>
          <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
        </div>
      ) : (
        <div className="text-gray-500">
          <Upload className="w-6 h-6 mx-auto mb-1 text-gray-400" />
          <p className="text-sm font-medium">Click to upload</p>
          <p className="text-xs text-gray-400 mt-0.5">{hint}</p>
          <p className="text-xs text-gray-400">JPG, PNG or PDF · max 5 MB</p>
        </div>
      )}
    </div>
    <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.pdf"
      className="hidden" onChange={onChange} />
    {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
  </div>
);

// ── Main modal component ──────────────────────────────────────────────────────

const EnhancedAuthModal = ({ isOpen, onClose, onLoginSuccess, initialMode = 'login', initialEmail = '', initialToken = '' }) => {
  const [mode, setMode] = useState(initialMode);
  const [accountType, setAccountType] = useState('regular');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Stored after registration so OTP verify knows which user
  const [pendingUserId, setPendingUserId] = useState('');
  const [pendingEmail, setPendingEmail] = useState(initialEmail || '');
  const [requiresApproval, setRequiresApproval] = useState(false);

  // New password fields for reset flow
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Verification file (student ID / org document)
  const [verificationFile, setVerificationFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  // OTP digits — 6 separate inputs for better UX
  const [otpDigits, setOtpDigits] = useState(
    initialToken && initialToken.length === 6 ? initialToken.split('') : ['', '', '', '', '', '']
  );
  const otpRefs = useRef([]);
  // Sync ref that's always current regardless of React render timing.
  // Controlled inputs' el.value reflects the last *rendered* value, not what
  // the user just typed if setState hasn't flushed yet — so we track it here.
  const otpValueRef = useRef(
    initialToken && initialToken.length === 6 ? initialToken.split('') : ['', '', '', '', '', '']
  );

  const [formData, setFormData] = useState({
    username: '',
    email: initialEmail || '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    institution: '',
    studentId: '',
    course: '',
    yearOfStudy: '',
    isStudent: false,
    organizationName: '',
    organizationRole: '',
    isAffiliated: false,
  });

  // Sync mode when prop changes (e.g. "Register" button sets initialMode='register')
  useEffect(() => {
    setMode(initialMode);
    setError('');
    setSuccess('');
    if (initialEmail) {
      setFormData(prev => ({ ...prev, email: initialEmail }));
      setPendingEmail(initialEmail);
    }
    if (initialToken && initialToken.length === 6) {
      otpValueRef.current = initialToken.split('');
      setOtpDigits([...otpValueRef.current]);
    }
  }, [initialMode, isOpen, initialEmail, initialToken]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleAccountTypeChange = (type) => {
    setAccountType(type);
    setFormData(prev => ({
      ...prev,
      isStudent: type === 'student',
      isAffiliated: type === 'organization',
    }));
  };

  // ── OTP digit input handlers ────────────────────────────────────────────────

  const handleOtpDigit = (index, value) => {
    const digit = value.replace(/\D/, '').slice(-1);
    otpValueRef.current[index] = digit;           // sync — always current
    setOtpDigits([...otpValueRef.current]);        // async — for UI re-render
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      otpValueRef.current = text.split('');
      setOtpDigits([...otpValueRef.current]);
      otpRefs.current[5]?.focus();
    }
    e.preventDefault();
  };

  const otp = otpDigits.join('');

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateRegistration = () => {
    if (!formData.firstName || !formData.lastName || !formData.username ||
        !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }
    if (!formData.phone) {
      setError('Phone number is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Enter a valid email address');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (accountType === 'student' && !formData.institution) {
      setError('Institution is required for student accounts');
      return false;
    }
    if (accountType === 'organization' && !formData.organizationName) {
      setError('Organization name is required');
      return false;
    }
    return true;
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError('');
    if (!file) { setVerificationFile(null); return; }
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      setFileError('Only JPG, PNG, or PDF files are accepted');
      setVerificationFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFileError('File must be smaller than 5 MB');
      setVerificationFile(null);
      return;
    }
    setVerificationFile(file);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateRegistration()) return;

    // Require file for student/organization
    if ((accountType === 'student' || accountType === 'organization') && !verificationFile) {
      setError(
        accountType === 'student'
          ? 'Please upload your student ID card'
          : 'Please upload your organization verification document'
      );
      return;
    }

    setLoading(true);
    try {
      const result = await authService.register(
        { ...formData, accountType },
        verificationFile
      );
      setPendingUserId(result.userId);
      setPendingEmail(formData.email);
      setRequiresApproval(result.requiresApproval || false);
      // Dev mode: server returns the OTP directly so testing works without email
      if (result.devOtp) {
        otpValueRef.current = String(result.devOtp).split('');
        setOtpDigits([...otpValueRef.current]);
        setSuccess(`[DEV] OTP auto-filled: ${result.devOtp}`);
      } else {
        otpValueRef.current = ['', '', '', '', '', ''];
        setOtpDigits(['', '', '', '', '', '']);
        setSuccess(`We sent a 6-digit code to ${formData.email}. Enter it below.`);
      }
      setTimeout(() => { setSuccess(''); setMode('verify-otp'); }, 1800);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.email || !formData.password) {
      setError('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const result = await authService.login(formData.email, formData.password);
      setSuccess('Login successful!');
      setTimeout(() => {
        if (onLoginSuccess) onLoginSuccess(result.user);
        onClose();
      }, 700);
    } catch (err) {
      // Backend returns userId hint when email not verified
      if (err.userId) {
        setPendingUserId(err.userId);
        setPendingEmail(formData.email);
        otpValueRef.current = ['', '', '', '', '', ''];
        setOtpDigits(['', '', '', '', '', '']);
        setError('');
        setSuccess('Your email is not verified. Enter the code we sent you.');
        setTimeout(() => { setSuccess(''); setMode('verify-otp'); }, 1800);
      } else {
        setError(err.message || 'Login failed. Check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    // Read from the sync ref — always reflects what the user actually typed,
    // even if React hasn't committed the state update yet.
    const currentOtp = otpValueRef.current.join('');
    if (currentOtp.replace(/\s/g, '').length < 6) { setError('Enter all 6 digits'); return; }
    if (!pendingUserId) { setError('Session expired. Please register again.'); return; }
    setLoading(true);
    try {
      const result = await authService.verifyEmail(pendingUserId, currentOtp);
      if (requiresApproval) {
        // Student/org — email verified but needs admin approval
        setSuccess('');
        setMode('pending-approval');
      } else {
        setSuccess('Email verified! Logging you in...');
        setTimeout(() => {
          if (onLoginSuccess) onLoginSuccess(result.user, { isNewRegistration: true });
          onClose();
        }, 900);
      }
    } catch (err) {
      setError(err.message || 'Invalid or expired code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingUserId || resending) return;
    setResending(true);
    setError('');
    try {
      await authService.resendOtp(pendingUserId);
      setSuccess('A new code has been sent to your email.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Could not resend code. Try again.');
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.email) { setError('Please enter your email address'); return; }
    setLoading(true);
    try {
      await authService.requestPasswordReset(formData.email);
      setPendingEmail(formData.email);
      otpValueRef.current = ['', '', '', '', '', ''];
      setOtpDigits(['', '', '', '', '', '']);
      setNewPassword('');
      setConfirmNewPassword('');
      setSuccess('Reset code sent! Check your email.');
      setTimeout(() => { setSuccess(''); setMode('reset-otp'); }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetOtp = (e) => {
    e.preventDefault();
    setError('');
    if (otp.length < 6) { setError('Enter all 6 digits'); return; }
    setMode('reset-password');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!newPassword || newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmNewPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authService.resetPassword(pendingEmail, otp, newPassword);
      setSuccess('Password set! You can now log in.');
      setTimeout(() => { setSuccess(''); setMode('login'); }, 1800);
    } catch (err) {
      setError(err.message || 'Failed to set password. The code may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // ── Form renderers ──────────────────────────────────────────────────────────

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <Mail className="w-4 h-4 inline mr-1.5 text-gray-400" />Email Address
        </label>
        <input type="email" name="email" value={formData.email} onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          placeholder="you@example.com" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <Lock className="w-4 h-4 inline mr-1.5 text-gray-400" />Password
        </label>
        <input type="password" name="password" value={formData.password} onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          placeholder="••••••••" required />
      </div>
      <div className="text-right">
        <button type="button" onClick={() => setMode('forgot-password')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          Forgot password?
        </button>
      </div>
      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50">
        {loading ? 'Logging in...' : 'Login'}
      </button>
      <p className="text-center text-sm text-gray-600">
        Don't have an account?{' '}
        <button type="button" onClick={() => setMode('register')}
          className="text-blue-600 hover:text-blue-700 font-semibold">Register</button>
      </p>
    </form>
  );

  const renderRegisterForm = () => (
    <form onSubmit={handleRegister} className="space-y-4">
      {/* Account type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { key: 'regular', label: 'Regular', Icon: User },
            { key: 'student', label: 'Student', Icon: GraduationCap },
            { key: 'organization', label: 'Organization', Icon: Building },
          ].map(({ key, label, Icon }) => (
            <button key={key} type="button" onClick={() => handleAccountTypeChange(key)}
              className={`p-3 border-2 rounded-lg transition-all text-center ${
                accountType === key ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
              }`}>
              <Icon className="w-5 h-5 mx-auto mb-1" />
              <div className="text-xs font-medium">{label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Name */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name *</label>
          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="John" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name *</label>
          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Doe" required />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Username *</label>
        <input type="text" name="username" value={formData.username} onChange={handleChange}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="johndoe" required />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
        <input type="email" name="email" value={formData.email} onChange={handleChange}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="you@example.com" required />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
        <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="+254 700 000 000" required />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Password *</label>
          <input type="password" name="password" value={formData.password} onChange={handleChange}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Min 8 characters" required minLength="8" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm *</label>
          <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Repeat password" required minLength="8" />
        </div>
      </div>

      {/* Institutional email notice for student/org */}
      {(accountType === 'student' || accountType === 'organization') && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <span>
            {accountType === 'student'
              ? 'Use your institutional/school email (e.g. student@uonbi.ac.ke). Gmail/Yahoo not accepted.'
              : 'Use your official organization email. Gmail/Yahoo not accepted.'}
          </span>
        </div>
      )}

      {/* Student fields */}
      {accountType === 'student' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-blue-900 text-sm flex items-center">
            <GraduationCap className="w-4 h-4 mr-2" />Student Information
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Institution *</label>
            <input type="text" name="institution" value={formData.institution} onChange={handleChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="University of Nairobi" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student ID No.</label>
              <input type="text" name="studentId" value={formData.studentId} onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="S12345" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year of Study</label>
              <select name="yearOfStudy" value={formData.yearOfStudy} onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                <option value="">Select</option>
                {[1,2,3,4,5].map(y => <option key={y} value={y}>Year {y}{y===5?'+':''}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course / Field of Study</label>
            <input type="text" name="course" value={formData.course} onChange={handleChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Environmental Science" />
          </div>

          {/* Student ID upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Student ID Card <span className="text-red-500">*</span>
            </label>
            <FileUploadBox
              file={verificationFile}
              error={fileError}
              inputRef={fileInputRef}
              onChange={handleFileChange}
              hint="Upload a photo or scan of your student ID card"
            />
          </div>
        </div>
      )}

      {/* Organization fields */}
      {accountType === 'organization' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-purple-900 text-sm flex items-center">
            <Building className="w-4 h-4 mr-2" />Organization Information
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
            <input type="text" name="organizationName" value={formData.organizationName} onChange={handleChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Kenya Meteorological Department" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Role</label>
            <input type="text" name="organizationRole" value={formData.organizationRole} onChange={handleChange}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Research Scientist" />
          </div>

          {/* Org verification document upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verification Document <span className="text-red-500">*</span>
            </label>
            <FileUploadBox
              file={verificationFile}
              error={fileError}
              inputRef={fileInputRef}
              onChange={handleFileChange}
              hint="Upload your organization registration certificate or official letterhead"
            />
          </div>
        </div>
      )}

      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50">
        {loading ? 'Creating Account...' : 'Create Account'}
      </button>

      <p className="text-center text-sm text-gray-600">
        Already have an account?{' '}
        <button type="button" onClick={() => setMode('login')}
          className="text-blue-600 hover:text-blue-700 font-semibold">Login</button>
      </p>
    </form>
  );

  const renderOTPForm = () => (
    <form onSubmit={handleVerifyOTP} className="space-y-6">
      {/* Email hint */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          <Mail className="w-8 h-8 text-blue-600" />
        </div>
        <p className="text-gray-700 font-medium">Check your inbox</p>
        <p className="text-gray-500 text-sm mt-1">
          We sent a 6-digit code to<br />
          <span className="font-semibold text-gray-800">{pendingEmail}</span>
        </p>
      </div>

      {/* 6-digit OTP boxes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
          Enter verification code
        </label>
        <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
          {otpDigits.map((digit, i) => (
            <input
              key={i}
              ref={el => otpRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              onChange={e => handleOtpDigit(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              className="w-11 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              style={{ borderColor: digit ? '#16a34a' : '#d1d5db' }}
            />
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">You can also paste the code</p>
      </div>

      <button type="submit" disabled={loading || otp.length < 6}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50">
        {loading ? 'Verifying...' : 'Verify Email'}
      </button>

      {/* Resend */}
      <div className="text-center space-y-2">
        <p className="text-sm text-gray-500">Didn't receive the code?</p>
        <button type="button" onClick={handleResendOtp} disabled={resending}
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
          {resending ? 'Sending...' : 'Resend code'}
        </button>
        <p className="block">
          <button type="button" onClick={() => setMode('register')}
            className="text-xs text-gray-400 hover:text-gray-600">
            Wrong email? Go back
          </button>
        </p>
      </div>
    </form>
  );

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <p className="text-gray-600 text-sm text-center">
        Enter your registered email and we'll send a reset code.
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
        <input type="email" name="email" value={formData.email} onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="you@example.com" required />
      </div>
      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50">
        {loading ? 'Sending...' : 'Send Reset Code'}
      </button>
      <p className="text-center">
        <button type="button" onClick={() => setMode('login')}
          className="text-sm text-blue-600 hover:text-blue-700 font-semibold">
          Back to Login
        </button>
      </p>
    </form>
  );

  const renderPendingApproval = () => (
    <div className="text-center space-y-5 py-4">
      <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
        <Clock className="w-10 h-10 text-amber-600" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900">Verification Pending</h3>
        <p className="text-gray-500 text-sm mt-1">
          Your email is confirmed. Our team will now review your submitted document.
        </p>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left space-y-2">
        <p className="text-sm font-semibold text-amber-900">What happens next?</p>
        <ul className="text-sm text-amber-800 space-y-1.5">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            Email verified — done
          </li>
          <li className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            Admin reviews your document (typically within 24–48 hours)
          </li>
          <li className="flex items-start gap-2">
            <Mail className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            You'll receive an email at <strong>{pendingEmail}</strong> once approved
          </li>
        </ul>
      </div>
      <button onClick={onClose}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors">
        Got it, I'll wait for approval
      </button>
    </div>
  );

  const renderResetOtpForm = () => (
    <form onSubmit={handleVerifyResetOtp} className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          <Mail className="w-8 h-8 text-blue-600" />
        </div>
        <p className="text-gray-700 font-medium">Check your inbox</p>
        <p className="text-gray-500 text-sm mt-1">
          We sent a 6-digit reset code to<br />
          <span className="font-semibold text-gray-800">{pendingEmail}</span>
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
          Enter reset code
        </label>
        <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
          {otpDigits.map((digit, i) => (
            <input
              key={i}
              ref={el => otpRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              onChange={e => handleOtpDigit(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              className="w-11 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              style={{ borderColor: digit ? '#16a34a' : '#d1d5db' }}
            />
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">You can also paste the code</p>
      </div>
      <button type="submit" disabled={otp.length < 6}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50">
        Continue
      </button>
      <p className="text-center">
        <button type="button" onClick={() => setMode('forgot-password')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Resend or use different email
        </button>
      </p>
    </form>
  );

  const renderResetPasswordForm = () => (
    <form onSubmit={handleResetPassword} className="space-y-4">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
          <Lock className="w-8 h-8 text-blue-600" />
        </div>
        <p className="text-gray-600 text-sm">Choose a new password for <strong>{pendingEmail}</strong></p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password *</label>
        <input type="password" value={newPassword} onChange={e => { setNewPassword(e.target.value); setError(''); }}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="Min 8 characters" minLength="8" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password *</label>
        <input type="password" value={confirmNewPassword} onChange={e => { setConfirmNewPassword(e.target.value); setError(''); }}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="Repeat password" minLength="8" required />
      </div>
      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50">
        {loading ? 'Saving...' : 'Set New Password'}
      </button>
      <p className="text-center">
        <button type="button" onClick={() => setMode('reset-otp')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Back
        </button>
      </p>
    </form>
  );

  const titles = {
    login:              ['Welcome Back',       'Login to access weather data'],
    register:           ['Create Account',     'Join the Climate Pulse network'],
    'verify-otp':       ['Verify Your Email',  'Check your inbox for the code'],
    'forgot-password':  ['Reset Password',     'Recover your account'],
    'reset-otp':        ['Check Your Email',   'Enter the 6-digit code we sent'],
    'reset-password':   ['Set New Password',   'Almost done — choose a new password'],
    'pending-approval': ['Submitted!',         'Your application is under review'],
  };
  const [title, subtitle] = titles[mode] || ['Authentication', ''];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{title}</h2>
              <p className="text-blue-100 text-sm mt-0.5">{subtitle}</p>
            </div>
            <button onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-blue-700 text-sm">{success}</p>
            </div>
          )}

          {mode === 'login'             && renderLoginForm()}
          {mode === 'register'          && renderRegisterForm()}
          {mode === 'verify-otp'        && renderOTPForm()}
          {mode === 'forgot-password'   && renderForgotPasswordForm()}
          {mode === 'reset-otp'         && renderResetOtpForm()}
          {mode === 'reset-password'    && renderResetPasswordForm()}
          {mode === 'pending-approval'  && renderPendingApproval()}
        </div>
      </div>
    </div>
  );
};

export default EnhancedAuthModal;
